import React, { useEffect, useState, useMemo } from "react";
import {
  Plus, UserPlus, FileText, Search, Calendar, Phone, Mail,
  Tv2, Hash, RefreshCw, Shield, ChevronDown, ChevronUp, Box,
} from "lucide-react";
import { api } from "@/lib/api";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
import { toast } from "sonner";
import SearchableSelect from "@/components/ui/SearchableSelect";

const STATUS_FILTERS = ["all", "pending", "approved", "rejected"];

export default function MyCustomers() {
  const [activeTab, setActiveTab] = useState("requests");
  const [customers, setCustomers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [plans, setPlans] = useState([]);
  const [modal, setModal] = useState(false);

  // Filters
  const [reqFilter, setReqFilter] = useState("all");
  const [custSearch, setCustSearch] = useState("");
  const [custOperator, setCustOperator] = useState("all");
  const [expandedCust, setExpandedCust] = useState(null);

  const [form, setForm] = useState({
    name: "", email: "", mobile: "", dth_id: "", box_number: "",
    operator: "Dish TV", plan_id: "", pincode: "", area: ""
  });

  const [loading, setLoading] = useState(true);
  const [showAllCustomers, setShowAllCustomers] = useState(false);
  const [assignmentFilter, setAssignmentFilter] = useState("all");

  const loadCustomers = () => api.get("/dealer/customers").then(r => setCustomers(r.data)).catch(() => { });
  const loadRequests = () => api.get("/dealer/registration-requests").then(r => setRequests(r.data)).catch(() => { });
  const loadPlans = () => api.get("/dealer/catalog?purpose=registration").then(r => {
    setPlans(r.data);
    if (r.data.length > 0) setForm(f => ({ ...f, plan_id: f.plan_id || r.data[0].id }));
  }).catch(() => { });

  useEffect(() => {
    api.get("/dealer/tenant-settings").then(r => {
      setShowAllCustomers(Boolean(r.data?.show_all_customers));
    }).catch(() => {});
    Promise.all([loadCustomers(), loadRequests(), loadPlans()]).finally(() => setLoading(false));
  }, []);

  const isValidMobile = (num) => /^[6-9]\d{9}$/.test(num);

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidMobile(form.mobile)) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    try {
      // Strip empty email so backend treats it as null
      const payload = { ...form };
      if (!payload.email || !payload.email.trim()) delete payload.email;
      // dealer_id is NOT sent — backend auto-uses the logged-in dealer's own ID as reference
      await api.post("/dealer/registration-requests", payload);
      toast.success("Registration request submitted!");
      setModal(false);
      setForm({ name: "", email: "", mobile: "", dth_id: "", box_number: "", operator: "Dish TV", plan_id: plans[0]?.id || "", pincode: "", area: "" });
      loadRequests();
      setActiveTab("requests");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to submit request"); }
  };

  const daysLeft = (planEnd) => {
    if (!planEnd) return null;
    return Math.ceil((new Date(planEnd) - new Date()) / 86400000);
  };

  // Filtered requests
  const filteredRequests = useMemo(() =>
    requests.filter(r => reqFilter === "all" || r.status === reqFilter),
    [requests, reqFilter]
  );

  // Filtered active customers
  const uniqueOperators = useMemo(() =>
    ["all", ...new Set(customers.map(r => r.connection?.operator).filter(Boolean))],
    [customers]
  );
  const filteredCustomers = useMemo(() =>
    customers.filter(r => {
      const matchOp = custOperator === "all" || r.connection?.operator === custOperator;
      const matchAssignment = !showAllCustomers || assignmentFilter === "all" ||
        (assignmentFilter === "assigned" && r.is_assigned) ||
        (assignmentFilter === "unassigned" && !r.is_assigned);
      const q = custSearch.toLowerCase();
      const matchSearch = !q ||
        r.customer?.name?.toLowerCase().includes(q) ||
        r.customer?.mobile?.includes(q) ||
        r.connection?.dth_id?.includes(q);
      return matchOp && matchAssignment && matchSearch;
    }),
    [customers, custOperator, custSearch, showAllCustomers, assignmentFilter]
  );

  if (loading) return <div className="text-slate-400 pt-8 text-center">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl text-brand-nav">My Customers</h2>
        <button onClick={() => setModal(true)}
          className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primaryHover text-white text-sm font-semibold px-4 py-2.5 rounded-lg shadow-sm">
          <Plus className="w-4 h-4" /> Registration Request
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-slate-200">
        <button onClick={() => setActiveTab("requests")} className={`pb-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'requests' ? 'border-brand-primary text-brand-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
          Registration Requests
          <span className="ml-1.5 text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{requests.length}</span>
        </button>
        <button onClick={() => setActiveTab("active")} className={`pb-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'active' ? 'border-brand-primary text-brand-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
          Active Customers
          <span className="ml-1.5 text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{customers.length}</span>
        </button>
      </div>

      {/* ── REQUESTS TAB ── */}
      {activeTab === "requests" && (
        <>
          {/* Status filter pills */}
          <div className="flex items-center gap-2 flex-wrap">
            {STATUS_FILTERS.map(s => (
              <button key={s} onClick={() => setReqFilter(s)}
                className={`px-3 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${reqFilter === s
                    ? s === "pending" ? "bg-amber-500 text-white"
                      : s === "approved" ? "bg-green-600 text-white"
                        : s === "rejected" ? "bg-red-500 text-white"
                          : "bg-brand-primary text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}>
                {s === "all" ? `All (${requests.length})` : `${s} (${requests.filter(r => r.status === s).length})`}
              </button>
            ))}
          </div>

          {filteredRequests.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
              <FileText className="w-8 h-8 mx-auto mb-3 text-slate-300" />
              <p>{reqFilter === "all" ? "No registration requests found." : `No ${reqFilter} requests.`}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredRequests.map((r, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-brand-nav">{r.name}</p>
                      <p className="text-xs text-slate-500">
                        {r.email || 'No email'} • {r.mobile ? (r.mobile.startsWith('******') ? r.mobile : ('******' + r.mobile.slice(-4))) : ''}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">Area: {r.area} ({r.pincode})</p>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${r.status === 'pending' ? 'bg-amber-50 text-amber-600' :
                        r.status === 'approved' ? 'bg-green-50 text-green-700' :
                          'bg-red-50 text-red-600'
                      }`}>
                      {r.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="mt-2 pt-2 border-t border-slate-100 flex gap-4 text-xs text-slate-500">
                    <span>DTH ID: <strong>{r.dth_id || 'Not assigned'}</strong></span>
                    <span>Op: {r.operator}</span>
                    <span>Plan: {r.plan?.name || 'Unknown'}</span>
                  </div>
                  {r.status === 'rejected' && r.rejection_reason && (
                    <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                      <strong>Reason:</strong> {r.rejection_reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── ACTIVE CUSTOMERS TAB ── */}
      {activeTab === "active" && (
        <>
          {/* Assignment filter pills (when showing all customers) */}
          {showAllCustomers && (
            <div className="flex items-center gap-2 flex-wrap pb-1">
              <button
                type="button"
                onClick={() => setAssignmentFilter("all")}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  assignmentFilter === "all"
                    ? "bg-brand-primary text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                All Customers ({customers.length})
              </button>
              <button
                type="button"
                onClick={() => setAssignmentFilter("assigned")}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  assignmentFilter === "assigned"
                    ? "bg-brand-primary text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                My Assigned ({customers.filter(c => c.is_assigned).length})
              </button>
              <button
                type="button"
                onClick={() => setAssignmentFilter("unassigned")}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  assignmentFilter === "unassigned"
                    ? "bg-brand-primary text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                Unassigned / Other ({customers.filter(c => !c.is_assigned).length})
              </button>
            </div>
          )}

          {/* Search + Operator filter */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                placeholder="Search by name, mobile, DTH ID…"
                value={custSearch}
                onChange={e => setCustSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
              />
            </div>
            <div className="w-48">
              <SearchableSelect value={custOperator} onChange={setCustOperator} options={uniqueOperators.map(op => ({ label: op === "all" ? "All Operators" : op, value: op }))} />
            </div>
          </div>

          {filteredCustomers.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
              <UserPlus className="w-8 h-8 mx-auto mb-3 text-slate-300" />
              <p>{customers.length === 0 ? "No active customers yet." : "No customers match your filters."}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredCustomers.map((r, i) => {
                const days = daysLeft(r.subscription?.plan_end);
                const isOpen = expandedCust === i;
                const regDate = r.customer_record?.created_at || r.customer?.created_at || r.connection?.created_at;
                return (
                  <div key={i} className={`bg-white rounded-xl border transition-colors ${isOpen ? 'border-brand-primary/30 shadow-sm' : 'border-slate-200 hover:border-slate-300'
                    }`}>
                    {/* Header — always visible */}
                    <div className="p-4 cursor-pointer" onClick={() => setExpandedCust(isOpen ? null : i)}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-brand-nav text-sm">{r.customer?.name || '—'}</p>
                            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${r.connection?.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'
                              }`}>{r.connection?.status || 'unknown'}</span>
                            {showAllCustomers && (
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                r.is_assigned
                                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                  : 'bg-amber-50 text-amber-700 border border-amber-200'
                              }`}>
                                {r.is_assigned ? 'Assigned to You' : 'Unassigned / Other'}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                            {r.customer?.mobile && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{r.customer.mobile}</span>}
                            {r.connection?.dth_id && <span className="flex items-center gap-1 font-mono"><Tv2 className="w-3 h-3" />{r.connection.dth_id}</span>}
                            <span className="flex items-center gap-1"><Hash className="w-3 h-3" />{r.connection?.operator}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {days !== null && (
                            <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${days < 0 ? 'bg-red-50 text-red-600' :
                                days <= 7 ? 'bg-red-50 text-red-600' :
                                  days <= 30 ? 'bg-amber-50 text-amber-600' :
                                    'bg-green-50 text-green-700'
                              }`}>
                              {days < 0 ? 'Expired' : `${days}d left`}
                            </span>
                          )}
                          {isOpen ? <ChevronUp className="w-4 h-4 text-brand-primary" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                        </div>
                      </div>
                    </div>

                    {/* Expanded details */}
                    {isOpen && (
                      <div className="px-4 pb-4 pt-0">
                        <div className="border-t border-slate-100 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* Customer Info */}
                          <div className="space-y-2">
                            <h6 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Customer Info</h6>
                            <div className="bg-slate-50 rounded-lg p-3 space-y-2 text-xs">
                              <div className="flex justify-between"><span className="text-slate-400">Name</span><span className="font-semibold text-slate-700">{r.customer?.name || '—'}</span></div>
                              <div className="flex justify-between"><span className="text-slate-400">Mobile</span><span className="font-mono text-slate-700">{r.customer?.mobile || '—'}</span></div>
                              <div className="flex justify-between"><span className="text-slate-400">Email</span><span className="text-slate-700 truncate ml-4">{r.customer?.email || '—'}</span></div>
                              <div className="flex justify-between"><span className="text-slate-400 flex items-center gap-1"><Calendar className="w-3 h-3" />Registered</span><span className="font-semibold text-brand-primary">{fmtDate(regDate)}</span></div>
                              <div className="flex justify-between"><span className="text-slate-400">Account Status</span>
                                <span className={`font-bold ${r.customer_record?.status === 'active' ? 'text-green-600' : 'text-red-500'}`}>{r.customer_record?.status || r.connection?.status || '—'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Connection Details */}
                          <div className="space-y-2">
                            <h6 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Connection Details</h6>
                            <div className="bg-slate-50 rounded-lg p-3 space-y-2 text-xs">
                              <div className="flex justify-between"><span className="text-slate-400">DTH / VC No</span><span className="font-mono font-semibold text-slate-700">{r.connection?.dth_id || '—'}</span></div>
                              <div className="flex justify-between"><span className="text-slate-400">Operator</span><span className="font-semibold text-slate-700">{r.connection?.operator || '—'}</span></div>
                              {r.connection?.box_number && <div className="flex justify-between"><span className="text-slate-400 flex items-center gap-1"><Box className="w-3 h-3" />Box No (STB)</span><span className="font-mono text-slate-700">{r.connection.box_number}</span></div>}
                              <div className="flex justify-between"><span className="text-slate-400">Connection Status</span>
                                <span className={`font-bold ${r.connection?.status === 'active' ? 'text-green-600' : 'text-slate-500'}`}>{r.connection?.status || '—'}</span>
                              </div>
                              {r.connection?.created_at && <div className="flex justify-between"><span className="text-slate-400">Added On</span><span className="text-slate-600">{fmtDate(r.connection.created_at)}</span></div>}
                            </div>
                          </div>

                          {/* Subscription Info — full width */}
                          <div className="sm:col-span-2 space-y-2">
                            <h6 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Subscription</h6>
                            {r.subscription ? (
                              <div className="bg-indigo-50/60 border border-indigo-100 rounded-lg p-3">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                  <div>
                                    <span className="text-slate-400 block">Plan</span>
                                    <span className="font-semibold text-brand-nav">{r.product?.name || '—'}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block">Start Date</span>
                                    <span className="font-semibold text-slate-700">{fmtDate(r.subscription.plan_start)}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block">End Date</span>
                                    <span className={`font-semibold ${days != null && days < 7 ? 'text-red-600' : 'text-slate-700'}`}>{fmtDate(r.subscription.plan_end)}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block">Status</span>
                                    <span className={`font-bold capitalize ${r.subscription.status === 'active' ? 'text-green-600' :
                                        r.subscription.status?.includes('expiring') ? 'text-amber-600' :
                                          'text-red-500'
                                      }`}>{r.subscription.status?.replace(/_/g, ' ') || '—'}</span>
                                  </div>
                                </div>
                                {(r.subscription.renewal_count > 0 || r.subscription.renewed_at) && (
                                  <div className="mt-2 pt-2 border-t border-indigo-100 flex items-center gap-3 text-[11px] text-slate-500">
                                    <span className="flex items-center gap-1"><RefreshCw className="w-3 h-3" />Renewals: <strong>{r.subscription.renewal_count || 0}</strong></span>
                                    {r.subscription.renewed_at && <span>Last renewed: {fmtDate(r.subscription.renewed_at)}</span>}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-400 italic">No active subscription</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── NEW REQUEST MODAL ── */}
      {modal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setModal(false)}>
          <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
            <h3 className="font-heading font-bold text-xl text-brand-nav">New Registration Request</h3>
            <p className="text-xs text-slate-400 mt-1">This request will be reviewed and approved by the Admin.</p>
            <div className="mt-4 space-y-3">
              <input required placeholder="Full Name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
              <div className="grid grid-cols-2 gap-3">
                <input type="email" placeholder="Email (Optional)" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
                <input required placeholder="Mobile *" type="tel" pattern="[6-9][0-9]{9}" maxLength={10}
                  value={form.mobile} onChange={e => setForm(p => ({ ...p, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                  className={`w-full border rounded-lg px-3 py-2.5 text-sm ${form.mobile && !isValidMobile(form.mobile) ? 'border-red-400 focus:ring-red-300/30' : 'border-slate-200 focus:ring-brand-primary/30'
                    } focus:outline-none focus:ring-2`} />
                {form.mobile && !isValidMobile(form.mobile) && (
                  <p className="text-xs text-red-500 mt-0.5">Enter valid 10-digit mobile</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Customer / DTH ID (Optional)" value={form.dth_id} onChange={e => setForm(p => ({ ...p, dth_id: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono" />
                <input placeholder="Box / STB No (Optional)" value={form.box_number} onChange={e => setForm(p => ({ ...p, box_number: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <SearchableSelect value={form.operator} onChange={val => setForm(p => ({ ...p, operator: val }))} options={[{ label: "Dish TV", value: "Dish TV" }]} />
                <SearchableSelect required value={form.plan_id} onChange={val => setForm(p => ({ ...p, plan_id: val }))} defaultLabel="Select DTH Plan *" options={plans.map(p => ({ label: `${p.name} — ₹${p.sell_price}`, value: p.id }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Pincode *" value={form.pincode} onChange={e => setForm(p => ({ ...p, pincode: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
                <input required placeholder="Area / Locality *" value={form.area} onChange={e => setForm(p => ({ ...p, area: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={() => setModal(false)} className="flex-1 border border-slate-200 text-slate-600 rounded-lg px-4 py-2.5 font-semibold hover:bg-slate-50">Cancel</button>
              <button type="submit" className="flex-1 bg-brand-accent hover:bg-brand-accentHover text-white rounded-lg px-4 py-2.5 font-semibold">Submit Request</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
